import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { logger } from '../utils/logger';
import { documentsApi, projectsApi, renderApi, companyDocumentsApi } from '../services/api';
import { ManifestEntry, ProjectInstance, CompanyDocument } from '../types';
import { 
  useToast, 
  Button, 
  Card, 
  Badge, 
  Label, 
  Input,
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';

export default function WizardPage() {
  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState<ProjectInstance[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [currentProject, setCurrentProject] = useState<ProjectInstance | null>(null);
  const [documents, setDocuments] = useState<ManifestEntry[]>([]);
  // Templates state kept for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [templates, setTemplates] = useState<CompanyDocument[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [docTitles, setDocTitles] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, string | number | boolean>>({});
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string>('');
  const [format, setFormat] = useState<'html' | 'pdf'>('html');
  
  const { success, error, warning } = useToast();

  useEffect(() => {
    loadProjects();
    loadDocuments();
    loadTemplates();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await projectsApi.list();
      setProjects(data);
    } catch (err) {
      logger.error('Failed to load projects:', err);
    }
  };

  const loadDocuments = async () => {
    try {
      const data = await documentsApi.list({ docType: 'plan' });
      setDocuments(data);
    } catch (err) {
      logger.error('Failed to load documents:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await companyDocumentsApi.list({ type: 'template', status: 'Released' });
      setTemplates(data);
    } catch (err) {
      logger.error('Failed to load templates:', err);
    }
  };

  const loadProject = async (projectId: string) => {
    try {
      const proj = await projectsApi.get(projectId);
      setCurrentProject(proj);
    } catch (err) {
      logger.error('Failed to load project:', err);
    }
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
    if (projectId) {
      loadProject(projectId);
    } else {
      setCurrentProject(null);
    }
  };

  const handleDocumentToggle = (docPath: string) => {
    setSelectedDocs(prev => {
      const isAdding = !prev.includes(docPath);
      
      if (isAdding) {
        // Add document and initialize title
        const docName = docPath.split('/').pop()?.replace('.html', '').replace(/_/g, ' ') || '';
        setDocTitles(prevTitles => ({
          ...prevTitles,
          [docPath]: currentProject?.projectTitle 
            ? `${docName} for ${currentProject.projectTitle}`
            : docName
        }));
        return [...prev, docPath];
      } else {
        // Remove document
        setDocTitles(prevTitles => {
          const newTitles = { ...prevTitles };
          delete newTitles[docPath];
          return newTitles;
        });
        return prev.filter(d => d !== docPath);
      }
    });
  };

  const handleDocTitleChange = (docPath: string, title: string) => {
    setDocTitles(prev => ({
      ...prev,
      [docPath]: title
    }));
  };

  const handleAnswerChange = (key: string, value: string | number | boolean) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handlePreview = async () => {
    if (selectedDocs.length === 0) {
      warning('No Documents Selected', 'Please select at least one document');
      return;
    }

    setLoading(true);
    try {
      const html = await renderApi.preview(selectedDocs[0], answers);
      setPreview(html);
      setStep(3);
    } catch (err) {
      error('Preview Failed', (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedProject) {
      warning('No Project Selected', 'Please select a project');
      return;
    }

    setLoading(true);
    try {
      // Check if project has document control
      if (currentProject?.projectCode) {
        // Register documents first and generate with document control
        for (const docPath of selectedDocs) {
          const docName = docPath.split('/').pop()?.replace('.html', '') || '';
          const docTitle = docTitles[docPath] || docName;
          
          try {
            // Register document (generates document number)
            const registeredDoc = await projectsApi.registerDocument(
              selectedProject,
              docPath,
              docName,
              docTitle,
              'User' // TODO: Get actual user from auth
            );

            // Prepare answers with document control tokens
            const docControlAnswers = {
              ...answers,
              'document.number': registeredDoc.documentNumber,
              'document.revision': 'Draft',
              'document.projectTitle': docTitle,
              'document.date': new Date().toLocaleDateString(),
              'document.status': 'Draft',
              'project.code': currentProject.projectCode,
              'project.title': currentProject.projectTitle,
              'project.customer': currentProject.customer,
              'project.program': currentProject.program
            };

            // Render document
            await renderApi.render({
              projectId: selectedProject,
              sourcePath: docPath,
              answers: docControlAnswers,
              format
            });

          } catch (docErr) {
            logger.error(`Failed to generate ${docPath}:`, docErr);
          }
        }
      } else {
        // Legacy mode - batch render without document control
        await renderApi.renderBatch(selectedProject, selectedDocs, answers, format);
      }

      success('Generation Complete', 'Documents generated successfully!');
      setStep(4);
    } catch (err) {
      error('Generation Failed', (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-8 pt-4 pb-8">
      <h1 className="page-title">Document Generation Wizard</h1>

      {/* Progress Indicator */}
      <Card className="p-6 mb-6">
        <div className="flex gap-3 items-center flex-wrap">
          <Badge variant={step >= 1 ? 'info' : 'warning'} className="whitespace-nowrap">
            1. Select Project & Documents
          </Badge>
          <Badge variant={step >= 2 ? 'info' : 'warning'} className="whitespace-nowrap">
            2. Provide Answers
          </Badge>
          <Badge variant={step >= 3 ? 'info' : 'warning'} className="whitespace-nowrap">
            3. Preview & Generate
          </Badge>
          <Badge variant={step >= 4 ? 'success' : 'warning'} className="whitespace-nowrap">
            4. Complete
          </Badge>
        </div>
      </Card>

      {/* Step 1: Select Project & Documents */}
      {step === 1 && (
        <div>
          <Card className="p-6 mb-6">
            <h2>Step 1: Select Project</h2>
            <div className="form-group">
              <Label>Project</Label>
              <RadixSelect 
                value={selectedProject} 
                onValueChange={handleProjectChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="-- Select Project --" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.projectCode ? ` (${p.projectCode})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </RadixSelect>
              <p className="form-help">
                Don't see your project? <a href="/projects">Create a new project</a>
              </p>
            </div>

            {currentProject && !currentProject.projectCode && (
              <div className="warning-box">
                <p className="m-0 text-sm">
                  <strong>⚠️ Legacy Project:</strong> This project was created without a project code. 
                  Document numbering will not be available. Consider creating a new project with document control.
                </p>
              </div>
            )}

            {currentProject && currentProject.projectCode && (
              <div className="info-box">
                <p className="m-0 text-sm">
                  <strong>Project Code:</strong> {currentProject.projectCode} | 
                  Documents will be numbered as: {currentProject.projectCode}-[TYPE]-001-Draft
                </p>
              </div>
            )}
          </Card>

          <Card className="p-6">
            <h2 className="break-words">Select Documents to Generate</h2>
            <div className="grid grid-2">
              {documents.map(doc => (
                <div key={doc.path} className={`p-4 border-2 rounded-lg overflow-hidden transition-all ${
                  selectedDocs.includes(doc.path)
                    ? 'border-primary dark:border-primary bg-info/10 dark:bg-info/20'
                    : 'border-border bg-card hover:border-border dark:hover:border-slate-500'
                }`}>
                  <Label className="flex items-start gap-3 cursor-pointer mb-2">
                    {/* eslint-disable-next-line no-restricted-syntax -- checkbox inputs require native element */}
                    <input
                      type="checkbox"
                      checked={selectedDocs.includes(doc.path)}
                      onChange={() => handleDocumentToggle(doc.path)}
                      className="flex-shrink-0 mt-1 w-4 h-4 accent-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold break-words text-foreground">
                        {doc.path.split('/').pop()?.replace('.html', '')}
                      </div>
                      <div className="text-sm text-muted-foreground break-words">
                        {doc.domains.join(', ')}
                      </div>
                    </div>
                  </Label>
                  
                  {selectedDocs.includes(doc.path) && currentProject?.projectCode && (
                    <div className="mt-3 pl-7 pt-3 border-t border-border">
                      <Label className="text-sm font-medium block mb-2">
                        Project-Specific Title:
                      </Label>
                      <Input
                        type="text"
                        value={String(docTitles[doc.path] || '')}
                        onChange={(e) => handleDocTitleChange(doc.path, e.target.value)}
                        placeholder={`e.g., ${doc.path.split('/').pop()?.replace('.html', '')} for ${currentProject.projectTitle || currentProject.name}`}
                        className="text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button 
                onClick={() => setStep(2)}
                disabled={selectedDocs.length === 0 || !selectedProject}
              >
                Next: Provide Answers
              </Button>
              {selectedDocs.length > 0 && (
                <span className="p-3 text-muted-foreground self-center break-words">
                  {selectedDocs.length} document{selectedDocs.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Step 2: Provide Answers */}
      {step === 2 && (
        <Card className="p-6">
          <h2 className="break-words">Step 2: Provide Answers</h2>
          <p className="mb-6 text-muted-foreground break-words">
            Fill in the information below. These values will be used to customize your documents.
          </p>

          <div className="form-group">
            <Label>Organization Name *</Label>
            <Input
              type="text"
              value={String(answers['organization.name'] || '')}
              onChange={(e) => handleAnswerChange('organization.name', e.target.value)}
              placeholder="e.g., Aerotage LLC"
            />
            <p className="form-help">Official legal or operating name of the organization</p>
          </div>

          <div className="form-group">
            <Label>Project Name *</Label>
            <Input
              type="text"
              value={String(answers['project.name'] || '')}
              onChange={(e) => handleAnswerChange('project.name', e.target.value)}
              placeholder="e.g., Example LRU 2025"
            />
          </div>

          <div className="form-group">
            <Label>Software Level (DAL) *</Label>
            <RadixSelect
              value={String(answers['software.level'] || '')}
              onValueChange={(value) => handleAnswerChange('software.level', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="-- Select Level --" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A">Level A (Catastrophic)</SelectItem>
                <SelectItem value="B">Level B (Hazardous)</SelectItem>
                <SelectItem value="C">Level C (Major)</SelectItem>
                <SelectItem value="D">Level D (Minor)</SelectItem>
                <SelectItem value="E">Level E (No Effect)</SelectItem>
              </SelectContent>
            </RadixSelect>
            <p className="form-help">Select per ARP4761 analysis and system safety allocation</p>
          </div>

          <div className="form-group">
            <Label>Certification Approach</Label>
            <RadixSelect
              value={String(answers['project.certification_approach'] || '')}
              onValueChange={(value) => handleAnswerChange('project.certification_approach', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="-- Select Approach --" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TSO">TSO (Technical Standard Order)</SelectItem>
                <SelectItem value="STC">STC (Supplemental Type Certificate)</SelectItem>
                <SelectItem value="TC">TC (Type Certificate)</SelectItem>
                <SelectItem value="PMA">PMA (Parts Manufacturer Approval)</SelectItem>
              </SelectContent>
            </RadixSelect>
          </div>

          <div className="flex gap-4 mt-8 flex-wrap">
            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button onClick={handlePreview} disabled={loading}>
              {loading ? 'Loading Preview...' : 'Preview Documents'}
            </Button>
          </div>
        </Card>
      )}

      {/* Step 3: Preview & Generate */}
      {step === 3 && (
        <div>
          <Card className="p-6 mb-6">
            <h2 className="break-words">Step 3: Preview & Generate</h2>
            
            <div className="form-group">
              <Label>Output Format</Label>
              <div className="flex gap-4">
                <Label className="flex items-center gap-2 cursor-pointer">
                  {/* eslint-disable-next-line no-restricted-syntax -- radio inputs require native element */}
                  <input
                    type="radio"
                    value="html"
                    checked={format === 'html'}
                    onChange={(e) => setFormat(e.target.value as 'html' | 'pdf')}
                  />
                  HTML
                </Label>
                <Label className="flex items-center gap-2 cursor-pointer">
                  {/* eslint-disable-next-line no-restricted-syntax -- radio inputs require native element */}
                  <input
                    type="radio"
                    value="pdf"
                    checked={format === 'pdf'}
                    onChange={(e) => setFormat(e.target.value as 'html' | 'pdf')}
                  />
                  PDF
                </Label>
              </div>
            </div>

            <div className="flex gap-4 flex-wrap">
              <Button variant="secondary" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button variant="success" onClick={handleGenerate} disabled={loading}>
                {loading ? 'Generating...' : `Generate ${selectedDocs.length} Document${selectedDocs.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </Card>

          {preview && (
            <Card className="p-6">
              <h3 className="break-words">Preview (First Document)</h3>
              <div 
                className="border border-border rounded-md p-4 max-h-[600px] overflow-auto bg-muted break-words"
                dangerouslySetInnerHTML={{ __html: preview }}
              />
            </Card>
          )}
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 4 && (
        <Card className="p-6">
          <h2>✅ Generation Complete!</h2>
          <p className="mt-4 break-words">
            {selectedDocs.length} document{selectedDocs.length !== 1 ? 's' : ''} generated successfully!
          </p>
          
          <div className="info-box mt-4">
            <p className="m-0">
              <strong>What's Next?</strong> View your generated documents in the project detail page.
              You can download them, create revisions, and submit for approval.
            </p>
          </div>

          <div className="mt-6 flex gap-4 flex-wrap">
            <Button asChild>
              <Link to={`/projects/${selectedProject}`}>
                View Project Documents
              </Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/projects">
                Back to Projects
              </Link>
            </Button>
            <Button variant="secondary" onClick={() => {
              setStep(1);
              setSelectedDocs([]);
              setDocTitles({});
              setAnswers({});
              setPreview('');
            }}>
              Generate More Documents
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
